/**
 * WHAT A STUDENT MEETS — the unsorted source, the bins, and one way to move between them.
 *
 * The canonical "what a student sees" layout for Sort, shared by the editor's live test surface and
 * Preview, the way Ordering's own test widget is for Ordering.
 *
 * ## Three layouts, authored rather than responsive
 *
 * Bin order is spatial and can carry meaning — Paleozoic at the bottom. Flipping a stacked sort
 * into columns because a phone turned sideways would destroy that axis, so the teacher declares
 * whether it matters: `vertical` and `horizontal` lock it, `responsive` says it does not and the
 * device may decide.
 *
 * Written with LOGICAL properties throughout (`inline-start`, not `left`), so the horizontal layout
 * flips correctly in a right-to-left language. Free now; a rewrite later.
 *
 * ## The phone shows the source and the bins as TABS
 *
 * Not one bin at a time — seeing what is in which bin IS the sort, so the bins stay together. It is
 * the SOURCE that steps aside, because it empties as the work proceeds and an empty strip competing
 * for height on a 390px screen is the one thing nobody needs. The carry survives the tab change,
 * which is the whole mechanic: pick up under Unsorted, switch, put down.
 *
 * ## Three inputs, one state machine
 *
 * Tap, keyboard and drag all drive `useSortPlacement`. Drag is wired so that it CANNOT produce an
 * outcome the other two cannot: a drag start is a pick-up, a drop is a place, and a drag that ends
 * nowhere leaves the item in hand rather than dropping it on the floor — which quietly turns a
 * failed drag into a successful pick-up instead of an error.
 *
 * There is deliberately NO `KeyboardSensor`. The carry already is the keyboard story, and dnd-kit's
 * keyboard sensor would claim the space bar on the same button, giving the keyboard two competing
 * models of what "select" means — the exact drift the state machine exists to prevent.
 */
import {
  DndContext, DragOverlay, MouseSensor, TouchSensor, useDroppable, useSensor,
  useSensors, type Announcements, type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pictureName, useAnimatedAssetIds } from './animated-image';
import { stripHtmlForTooltip } from './formula-plaintext';
import { OrderingItemMediaContent } from './ordering-item-media';
import { SOURCE, resolveDrop, sortCollisionDetection, unzone, useSortPlacement, zoneId } from './sort-placement';
import { useIsPhone } from './use-viewport';
import { ANSWER_COLUMN_CLASS, AnswerDisplayFrame, PreviewHtml } from './variable-chip-preview';

import type { SortBin, SortItem } from '@agavi/blocks';

import { SegmentedControl } from '@/components/ui/segmented-control';

export { SOURCE } from './sort-placement';

/**
 * How many bins a COLUMN layout can hold before the columns stop being readable.
 *
 * Lives here, beside the layout that enforces it, rather than in the block that offers the radio
 * — both need it, and the block is the one that imports this file.
 */
export const MAX_HORIZONTAL_BINS = 4;

/** Fisher-Yates, as Ordering's own shuffle. */
function shuffled<T>(xs: T[]): T[] {
  const arr = [...xs];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

interface SortAnswerDisplayProps {
  question: string;
  items: SortItem[];
  bins: SortBin[];
  /** The student's own partition, not the key. */
  placements: Record<string, string[]>;
  onChange: (placements: Record<string, string[]>) => void;
  layout: 'vertical' | 'horizontal' | 'responsive';
  /** The source goes on the far side of the bins. */
  inverted: boolean;
  /**
   * ORDER INSIDE A BIN IS GRADED, which is the only thing that makes a position worth speaking.
   *
   * `sort-placement.ts` already said announcements name the bin and not the position, "unless the
   * teacher turned it on" — and nothing implemented the exception, so with this on a listener was
   * told which bin an item reached and never where in it, while that position decided the mark.
   * Off, a position is noise: nothing grades it, and every move would carry a number that means
   * nothing. That an ordered sort IS ordered belongs in the question text, which is the teacher's
   * to write — this only stops the interface withholding what it then grades.
   */
  orderWithinBins?: boolean;
  /** Lay the items ACROSS each group instead of down it — see `SortBlockData.itemsAcross`. The
   *  `vertical` layout only: a horizontal bin IS a column, and `responsive` declines to assert an
   *  axis at all. */
  itemsAcross?: boolean;
  /**
   * THE ROLL FOR THIS SURFACE, and `null` is not `{}`.
   *
   * `null` is display mode, where a variable and an answer reference both stay chips. `{}` reads as
   * "a test surface that has rolled nothing", which leaves a variable a chip — so it LOOKS right —
   * and tells an answer reference to resolve to a sample anyway. That is the trap
   * `AnswerDisplayFrame` spells out in its own type, and the card hit it: a recall chip in an item
   * showed a number on an unselected card while every variable beside it stayed a pill.
   */
  rolledValues: Record<string, string> | null;
  disabled?: boolean;
  /** Bumped on REROLL, to re-scramble. Reset leaves the order alone — see `resetTest`. */
  shuffleGeneration?: number;
  /**
   * The leader's stem, ghosted above the question, when this block follows a scenario.
   *
   * A teacher's testing aid and never part of what a student is shown — in Preview the leader is
   * itself on the page rendering its drawn variant, so a copy here would print it twice. The hook
   * owns that rule; this just renders what it is handed.
   */
  scenario?: React.ReactNode;
  children?: React.ReactNode;
}

export function SortAnswerDisplay({
  question, items, bins, placements, onChange, layout, inverted, orderWithinBins,
  rolledValues, disabled, shuffleGeneration = 0, scenario, children, itemsAcross,
}: SortAnswerDisplayProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  /**
   * WHICH PHONE TAB OPENS, seeded from whether anything is actually unsorted.
   *
   * Always opening on the source was right for a student, whose source starts full, and silently
   * wrong for every surface showing a FINISHED arrangement: the collapsed card's key preview opened
   * on "Unsorted (0)" and drew an empty box, so a complete answer key read as a blank — the one
   * thing the card exists to show. Caught on a phone, where the tabs are the only layout.
   *
   * The SEED only, never an effect: a tab that moved on its own as the last item left the source
   * would yank the surface out from under whoever was placing it.
   */
  const [tab, setTab] = useState<'source' | 'bins'>(() => {
    const placedAtMount = new Set(Object.values(placements).flat());
    return items.some(i => !placedAtMount.has(i.id)) ? 'source' : 'bins';
  });
  /** What a carry says as it moves — a live region, since neither a tab change nor a placement
   *  moves focus on its own. */
  const [announcement, setAnnouncement] = useState('');
  /** Separate from the carry on purpose. A drag SETS the carry, but while the pointer still holds
   *  the item the carrying bar and the place-here buttons stay hidden: showing them mid-drag would
   *  reflow the page under dnd-kit's measured rects and the drop would land in the wrong bin. */
  const [dragging, setDragging] = useState<string | null>(null);

  const byId = new Map(items.map(i => [i.id, i]));
  /**
   * RECONCILED AGAINST THE LIVE BINS, exactly as `reconcileKey` does for the answer key.
   *
   * Deleting a bin in the editor while the test widget held items in it made those items vanish:
   * the bin stopped rendering, but the placements map still listed them, so they counted as
   * "placed" and were filtered out of the source too — in a bin that no longer existed and in no
   * bin at all. They came back on Reset, which cleared the map. An item whose bin is gone belongs
   * in Unsorted, which is what dropping the dead keys produces here.
   *
   * Read-side only, and that is enough: `place` rebuilds the map from `bins`, so the first
   * interaction after this writes the dead keys out for good.
   *
   * AND AGAINST THE LIVE ITEMS, which is the same reconciliation one level down and was missing.
   * Deleting an ITEM that the test widget had already placed left it sitting in its bin: the
   * source list filters by `byId`, so an unplaced deleted item disappeared correctly, but a
   * placed one was read straight out of this map and rendered — a deleted item a teacher could
   * still see, and still grade against, until Reset.
   */
  const livePlacements = Object.fromEntries(
    Object.entries(placements)
      .filter(([binId]) => bins.some(b => b.id === binId))
      .map(([binId, ids]) => [binId, ids.filter(id => byId.has(id))]),
  );
  const placed = new Set(Object.values(livePlacements).flat());

  /**
   * SCRAMBLED, and not optionally.
   *
   * The authoring surface groups items under their bins, which invites authoring them bin by bin —
   * so an unshuffled source would display the answer key as an arrangement. Re-scrambled on Reset
   * and never between, so the source does not reorder itself under a student mid-decision.
   */
  const sourceOrder = useMemo(
    () => shuffled(items.map(i => i.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items.length, shuffleGeneration],
  );
  const unsorted = sourceOrder.filter(id => !placed.has(id) && byId.has(id));

  /* `responsive` follows the device; the other two are the teacher saying the axis means something.
     Columns use `flex-row`, which inherits the document's text direction — so inline-start is the
     first bin in both a left-to-right and a right-to-left language, with no second rule.
     DECLARED HERE, above `itemBody`, which reads it to size a media item. Below, it was a `const`
     in its temporal dead zone by the time the first media item rendered, and the whole student
     surface threw — invisible to every unit test, because none of them rendered media HERE. */
  /* RESPONSIVE MUST RESPECT THE SAME CAP. `horizontal` is offered as a disabled radio past
     `MAX_HORIZONTAL_BINS`, because that many columns are too narrow to read — but `responsive`
     produced the identical column layout on a desktop and was never checked against it, so six
     bins plus the source rendered seven slivers. The cap is about how narrow a column may get,
     which does not depend on how the layout was chosen. */
  const columns = (layout === 'horizontal' || (layout === 'responsive' && !isPhone))
    && bins.length <= MAX_HORIZONTAL_BINS;
  /* VERTICAL ONLY, and `responsive` is excluded on purpose rather than by oversight: it is the
     teacher declining to assert an axis, and this is an assertion about one — see the field's own
     note. Gating on `!columns` instead would have let a stored `true` take effect in responsive
     portrait and not in responsive landscape, and in a horizontal sort past the bin cap, where the
     control reads as off. The setting is never CLEARED on a switch away, so it comes back. */
  const across = !!itemsAcross && layout === 'vertical';

  /* Which items MOVE — asked of the assets, since an item stores only an id. See `pictureName`. */
  const animatedAssetIds = useAnimatedAssetIds(
    useMemo(() => items.map(i => i.media?.assetId).filter((a): a is string => !!a), [items]),
  );

  /**
   * A MEDIA item is named by its ALT TEXT, never by `text`.
   *
   * `text` on a media item is the teacher's internal label — storage, so a recorded answer can be
   * read back later, and the picker seeds it from the asset's filename. Reading it here announced
   * that filing name to a listener and dropped the alt text, the one string written FOR them: a
   * probe item whose picture said "A poofy thing" was picked up as "Two". Both Ordering shapes had
   * the identical fault and fixed it this way, so a picture is named the same in every block that
   * can hold one. Everything spoken here routes through this: pickedUp, placed, returned,
   * cancelled, the carrying bar, and an audio item's Play button.
   */
  /*
   * Whether the button will name itself from what is inside it — see the label below.
   *
   * TEXT ONLY, and media deliberately does not count. A picture names the control through its
   * `alt`, but only once it has loaded: before that, and if it never does, the item renders a
   * box with nothing in it and the control has no name. `nameOf` reads the SAME alt text from
   * the stored media, so naming a media item outright is both earlier and no less accurate.
   */
  const speaksForItself = (id: string) => {
    const item = byId.get(id);
    /* A MEDIA ITEM RENDERS THE MEDIA, not the text — `text` on one is the teacher's internal
       label, as the note on `nameOf` says, and never reaches the page. So its contents name it
       only once the asset has loaded, and not at all if it never does. */
    if (item?.media) return false;
    return !!(item?.text && stripHtmlForTooltip(item.text).replace(/\s+/g, ' ').trim());
  };

  const nameOf = (id: string) => {
    const item = byId.get(id);
    if (item?.media?.kind === 'image') {
      const named = pictureName(item.media.altText, item.media.motionDescription,
        animatedAssetIds.has(item.media.assetId), t);
      if (named) return named;
    }
    /* Audio names itself by position only, deliberately — see MCQ's `mediaAccessibleName` for why
       a transcript specific enough to tell two clips apart gives away the answer. */
    if (item?.media?.kind === 'audio') return t('blocks.sort.untitledItem');
    const plain = item?.text ? stripHtmlForTooltip(item.text).replace(/\s+/g, ' ').trim() : '';
    return plain || t('blocks.sort.untitledItem');
  };
  const binName = (bin: SortBin) =>
    bin.label.replace(/<[^>]*>/g, '').trim() || t('blocks.sort.unnamedBin');
  /** A section's spoken name, source included, so one announcement string covers both. */
  const zoneName = (id: string) => {
    if (id === SOURCE) return t('blocks.sort.sourceHeading');
    const bin = bins.find(b => b.id === id);
    return bin ? binName(bin) : t('blocks.sort.unnamedBin');
  };

  const place = (itemId: string, binId: string, atIndex?: number) => {
    const next: Record<string, string[]> = {};
    for (const bin of bins) next[bin.id] = (livePlacements[bin.id] ?? []).filter(id => id !== itemId);
    if (binId !== SOURCE) {
      const list = [...(next[binId] ?? [])];
      list.splice(atIndex ?? list.length, 0, itemId);
      next[binId] = list;
    }
    onChange(next);
    if (binId === SOURCE) {
      setAnnouncement(t('blocks.sort.a11y.returned', { item: nameOf(itemId) }));
      return;
    }
    /* WHERE IT LANDED, not just which bin — but only when the landing place is graded. Read off
       `next`, the partition just written, rather than the stale `placements` prop. */
    const landed = next[binId] ?? [];
    setAnnouncement(orderWithinBins
      ? t('blocks.sort.a11y.placedAt', {
          item: nameOf(itemId), bin: zoneName(binId),
          position: landed.indexOf(itemId) + 1, total: landed.length,
        })
      : t('blocks.sort.a11y.placed', { item: nameOf(itemId), bin: zoneName(binId) }));
  };

  /** An item's position in whichever bin holds it, or null when it is still in the source. */
  const positionOf = (itemId: string) => {
    for (const bin of bins) {
      const list = livePlacements[bin.id] ?? [];
      const i = list.indexOf(itemId);
      if (i >= 0) return { position: i + 1, total: list.length };
    }
    return null;
  };

  const carry = useSortPlacement(place);
  /** Hidden while a pointer drag is in flight — see `dragging`. */
  const showCarryUi = carry.carrying !== null && dragging === null && !disabled;

  function tapItem(itemId: string) {
    if (disabled) return;
    const wasCarrying = carry.carrying === itemId;
    carry.toggle(itemId);
    setAnnouncement(wasCarrying
      ? t('blocks.sort.a11y.cancelled', { item: nameOf(itemId) })
      : t('blocks.sort.a11y.pickedUp', { item: nameOf(itemId) }));
  }

  // ── Drag ────────────────────────────────────────────────────────────────────
  // No KeyboardSensor: see the file's own doc comment. The touch delay is longer than Ordering's
  // because here a short tap already MEANS something — picking the item up — so the press has to be
  // deliberate enough that it cannot be mistaken for one.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );

  /** Which list an item is currently sitting in, by section id. */
  const zoneOf = (itemId: string) =>
    bins.find(bin => (livePlacements[bin.id] ?? []).includes(itemId))?.id ?? SOURCE;
  const dropContext = { binIds: bins.map(b => b.id), placements, isItem: (id: string) => byId.has(id) };

  /* dnd-kit narrates the drag itself — the pick-up and what is under the pointer, neither of which
     the carry can know. The OUTCOME is left to this component's own live region, so "Moved Granite
     to Igneous" is the same sentence whether it came from a tap, the keyboard or a drop. */
  const accessibility: { screenReaderInstructions: { draggable: string }; announcements: Announcements } = {
    screenReaderInstructions: { draggable: t('blocks.sort.a11y.dragInstructions') },
    announcements: {
      onDragStart: ({ active }) => t('blocks.sort.a11y.pickedUp', { item: nameOf(String(active.id)) }),
      onDragOver: ({ over }) => {
        if (!over) return undefined;
        const id = String(over.id);
        return t('blocks.sort.a11y.over', { bin: zoneName(unzone(id) ?? zoneOf(id)) });
      },
      onDragEnd: () => undefined,
      onDragCancel: () => undefined,
    },
  };

  function onDragStart({ active }: DragStartEvent) {
    setDragging(String(active.id));
    carry.pickUp(String(active.id));
  }

  function onDragEnd({ active, over }: DragEndEvent) {
    setDragging(null);
    const target = over ? resolveDrop(String(over.id), String(active.id), dropContext) : null;
    if (target) { carry.placeIn(target.binId, target.atIndex); return; }
    /**
     * A POINTER DRAG THAT LANDS NOWHERE IS AN ABORT, and is put back.
     *
     * It used to stay in hand, on the reasoning that the student had already said which item they
     * meant. What that produced on a desktop was a dropped drag followed by a carrying bar and a
     * "Place in ..." button appearing in every bin — controls a mouse user never asked for and
     * reads as a stuck state.
     *
     * Only pointer drags reach here: there is no `KeyboardSensor` (see this file's own note), and
     * tap-to-carry never goes through `onDragEnd`. So the carry UI stays exactly where it is the
     * only way to place — keyboard and touch — and making it touch-only, which was the first
     * instinct, would have taken the keyboard path away with it.
     */
    onDragCancel();
  }

  function onDragCancel() {
    const held = carry.carrying;
    setDragging(null);
    carry.cancel();
    if (held !== null) setAnnouncement(t('blocks.sort.a11y.cancelled', { item: nameOf(held) }));
  }

  // ── Pieces ──────────────────────────────────────────────────────────────────
  const itemClass = (held: boolean) =>
    `flex min-w-0 items-center gap-2 rounded-md border px-2 py-1.5 text-start text-sm transition-colors focus-visible:[outline:2px_auto] ${
      held ? 'border-ws-primary bg-ws-primary/10 text-ws-text' : 'border-ws-border bg-ws-btn text-ws-text hover:bg-ws-hover'
    } ${disabled
      /* `pointer-events-none`, and it is not decoration. The ONE disabled sort display is the key
         preview on the collapsed card, and that card is a button — on a phone it is the only way
         into the editor at all. A disabled `<button>` swallows a click rather than passing it on,
         so every tap that landed on a preview item, or on its picture, did nothing: on an eight
         item sort that is most of the card. Measured in a real browser — a tap at the card's top
         opened the editor, a tap at its centre hit an `IMG` and opened nothing.
         `cursor-not-allowed` goes with it, because it could never be seen again and the card's own
         cursor is the honest one: the thing you can actually click IS the card. */
      ? 'pointer-events-none opacity-60'
      : 'cursor-grab active:cursor-grabbing'}`;

  /* Media REPLACES the text, which stays the internal Label — the same contract MCQ's and
     Ordering's media options carry. Drawn by Ordering's own renderer rather than a second one:
     the file is generic, and it already handles the signed-URL fetch, the load failure and the
     animated-image controls. */
  const itemBody = (id: string) => {
    const item = byId.get(id);
    if (item?.media) {
      return (
        <span className='flex min-w-0 flex-1 items-center'>
          {/* `fillWidth` in COLUMNS: a bin column is a width the layout already chose, so a photo
              smaller than it sat as a thumbnail with room to spare. Stacked, the row is as wide as
              the block and the image keeps its own size. */}
          {/* THREE SIZINGS, one per layout, and each one is the container answering "who chose this
              width". In COLUMNS the bin did, so the image fills it. ACROSS, nothing did — the item
              is as wide as its content — so a tall photo would set the height of the whole row and
              the row would stop reading as a row; it takes the height cap instead, the same one
              the circular ring uses for the same reason. STACKED, the row is as wide as the block
              and the teacher's own choice of size stands. */}
          <OrderingItemMediaContent media={item.media} fillWidth={columns} constrained={across}
            ownerName={nameOf(id)} />
        </span>
      );
    }
    return (
      <PreviewHtml
        /* `PreviewHtml` alone: it resolves against the roll this subtree was given, so a second
           explicit pass here would be the same rule written twice, free to disagree. */
        html={item?.text ?? ''}
        as='span'
        className='min-w-0 flex-1 break-words'
      />
    );
  };

  const itemButton = (id: string) => {
    /*
     * NAMED WITH ITS POSITION once order is graded, and by its content alone otherwise.
     *
     * Position is structure and belongs in `aria-posinset`/`aria-setsize` by rule 5 — but those
     * are not valid on a plain `button`, which is what an item is here, so it goes in the name the
     * way Ordering's linear row does. An item in the SOURCE has no position worth speaking: it is
     * not in a bin, and nothing grades the order of things not yet sorted.
     */
    const at = orderWithinBins ? positionOf(id) : null;
    return (
      <SortItemButton
        key={id}
        id={id}
        held={carry.isCarrying(id)}
        disabled={!!disabled}
        className={itemClass(carry.isCarrying(id))}
        onTap={() => tapItem(id)}
        /*
         * NAMED BY ITS CONTENTS, except when it HAS none.
         *
         * `undefined` is deliberate and right for an ordinary item: the text is inside the
         * button, so the control names itself, and an `aria-label` would replace authored
         * markup with a stripped copy of it. What it was wrong for is an item with nothing
         * inside — no text yet, no media — where the control then had no accessible name at
         * all and a reader announced "button, sortable" and stopped. Found by scanning every
         * block's editor; the fixture's own untouched item is exactly this shape.
         *
         * So the fallback fires only on that case, and `nameOf` already has the right word for
         * it. An item whose media FAILED to load is not here: its placeholder carries the
         * teacher's alt text, so its contents speak.
         */
        label={at ? t('blocks.sort.a11y.itemAtPosition', { item: nameOf(id), ...at })
          : speaksForItself(id) ? undefined : nameOf(id)}
      >
        {itemBody(id)}
      </SortItemButton>
    );
  };

  /**
   * A section is a plain container with an explicit place-here button, NOT a giant button with the
   * items nested inside it. Nesting them put a button inside a button: invalid, and assistive
   * technology does not reliably reach the inner one — the keyboard path would have been the first
   * thing to break.
   */
  /**
   * WHERE A CARRIED ITEM CAN LAND, once the landing place is graded.
   *
   * One control per section appends, which is all a bin needs when order means nothing. With
   * `orderWithinBins` on it is not enough: a drag can already drop BETWEEN two items, so tap and
   * keyboard could reach an arrangement the pointer could and they could not — the one thing this
   * file's state machine exists to prevent. A keyboard user could only append, and fixing a
   * mistake meant taking the item out and putting it back.
   *
   * Indices are counted on the list WITH THE CARRIED ITEM REMOVED, because `place` removes it from
   * every bin before splicing. Counting them on the list as displayed would be off by one for any
   * item moved further down the bin it is already in.
   */
  const slotsFor = (binId: string) => {
    const without = (livePlacements[binId] ?? []).filter(id => id !== carry.carrying);
    return [
      ...without.map((id, index) => ({ index, label: t('blocks.sort.a11y.placeBefore', { item: nameOf(id) }) })),
      {
        index: without.length,
        /* An empty bin has no end to be at — it is just the bin. */
        label: without.length
          ? t('blocks.sort.a11y.placeHereEnd', { bin: zoneName(binId) })
          : t('blocks.sort.a11y.placeHere', { bin: zoneName(binId) }),
      },
    ];
  };

  const slotClass = 'rounded-md border border-dashed border-ws-primary bg-ws-primary/5 px-2 py-1 text-xs font-medium text-ws-text hover:bg-ws-primary/10';

  const section = (id: string, heading: React.ReactNode, ids: string[], extra = '') => (
    <SortZone
      key={id}
      id={id}
      armed={carry.carrying !== null && !disabled}
      className={extra}
      heading={heading}
      /* Appending only, which is the whole story for a section whose order is not graded — and
         for the source, which has no order at all. The graded case puts its targets BETWEEN the
         items instead, below, because that is where the choice actually is. */
      placeHere={showCarryUi && !(orderWithinBins && id !== SOURCE)
        ? (
          <button type='button' onClick={() => carry.placeIn(id)} className={`mt-1.5 ${slotClass}`}>
            {t('blocks.sort.a11y.placeHere', { bin: zoneName(id) })}
          </button>
        )
        : null}
    >
      {/* `rectSortingStrategy` for a WRAPPED row, not the horizontal list strategy: the list one
          assumes a single line and shifts along one axis, so an item dragged across a wrap point
          animated sideways off its row. The rect strategy measures actual boxes, which is what a
          wrap needs. */}
      <SortableContext items={ids} strategy={across ? rectSortingStrategy : verticalListSortingStrategy}>
        {/* A visible floor and a label even when empty, or an untouched sort reads as broken. */}
        {/* FIXED HEIGHT IN COLUMNS, scrolling past it.
            With `items-stretch` every column takes the tallest one's height, so dropping an item
            anywhere resized every bin on screen — the whole row jumped on each placement, which
            is distracting while aiming the next one. A column layout gets a ceiling and scrolls
            instead; a stacked layout keeps growing, which is what vertical space is for. */}
        {/* ACROSS WRAPS, and `items-start` with it: without an explicit cross-axis start every item
            in a wrapped row stretches to the tallest one in that row, so a two-line label beside a
            picture inflated both. `content-start` keeps the wrapped rows packed to the top of the
            bin rather than spread down its min-height. */}
        <div className={`mt-1.5 flex min-h-9 gap-1.5 ${
          across ? 'flex-row flex-wrap content-start items-start' : 'flex-col'} ${
          columns ? 'max-h-64 overflow-y-auto pr-0.5' : ''}`}>
          {showCarryUi && orderWithinBins && id !== SOURCE
            /*
             * INTERLEAVED, so a target sits where the item would go rather than describing it
             * from the bottom of the bin. Reading order IS the arrangement, which is what a
             * listener stepping through the bin is building.
             *
             * EVERY item still renders, the carried one included. Dropping it from the list while
             * it was in hand destroyed the element the keyboard was focused on — focus fell to
             * `body`, and the Escape that cancels a carry never reached it. A held item already
             * renders in place in the ungraded case; this just does not deviate from that.
             *
             * It gets no slot before it: `slotsFor` counts the list without it, so its own
             * position is not an offer, and a slot named after itself would be meaningless.
             */
            ? (() => {
                const slots = slotsFor(id);
                let next = 0;
                const out = ids.flatMap((itemId) => {
                  if (itemId === carry.carrying) return [itemButton(itemId)];
                  const slot = slots[next++]!;
                  return [
                    <button key={`slot-${slot.index}`} type='button'
                      onClick={() => carry.placeIn(id, slot.index)} className={slotClass}>
                      {slot.label}
                    </button>,
                    itemButton(itemId),
                  ];
                });
                const end = slots[slots.length - 1]!;
                return [...out, (
                  <button key={`slot-${end.index}`} type='button'
                    onClick={() => carry.placeIn(id, end.index)} className={slotClass}>
                    {end.label}
                  </button>
                )];
              })()
            : ids.map(itemButton)}
        </div>
      </SortableContext>
    </SortZone>
  );

  /**
   * EVERY COLUMN TAKES AN EQUAL SHARE, with a floor.
   *
   * The source passed no flex class, so it was `flex: 0 1 auto` and sized itself to its content
   * while the bins — `flex-1`, basis zero — split whatever was left. With six items in the source
   * and four bins that left the bins a few pixels each: named "Fruits" and "Equations" rendered as
   * vertical slivers with their headings spilling outside them, and an empty bin collapsed to
   * nothing at all. The floor is what stops a column disappearing, and the row scrolls rather than
   * crushing them when the floor does not fit.
   *
   * NO FLOOR AND NO SCROLL. An equal share is enough on its own: the collapse was caused by the
   * source not being a flex child at all, not by the columns being too many. A `min-w` floor past
   * the container's width makes the row overflow, and `overflow-x-auto` then paints a scrollbar
   * under every sort on a desktop that has room to spare. The bin cap is what keeps a column
   * readable; this just divides the space.
   *
   * Only in columns: `flex-1` down a stacked layout would make each bin grow to fill height.
   */
  const columnClass = columns ? 'flex-1' : '';

  const sourceSection = section(
    SOURCE,
    <span className='text-xs font-semibold uppercase tracking-wider text-ws-muted'>
      {t('blocks.sort.sourceHeadingCount', { count: unsorted.length })}
    </span>,
    unsorted,
    columnClass,
  );

  const binSections = bins.map(bin => section(
    bin.id,
    (
      <>
        {/* WHAT THE NAME IS A NAME OF. The group is labelled BY this heading, so a bin called
            "Fruits" announced itself as the bare word "Fruits" — a listener entering the group
            learned the word and not that it was a place to put things. The prefix is `sr-only`
            rather than an `aria-label` on purpose: an `aria-label` would replace the name with
            text that is not on screen, and rule 9 of `docs/accessible-names.md` is that a control
            must CONTAIN what names it. Visibly the column header is still what the teacher typed. */}
        <span className='sr-only'>{t('blocks.sort.a11y.binCategory')}</span>
        {/* CENTRED over its own column. A bin name is a column header, and left-aligning it over a
            narrow column read as the first row of content rather than as its label. `block` so the
            centring has a box to work in — a `span` shrinks to its text and cannot centre. */}
        <PreviewHtml html={bin.label} as='span'
          className='block text-center text-sm font-semibold text-ws-text' />
      </>
    ),
    livePlacements[bin.id] ?? [],
    columnClass,
  ));


  return (
    <AnswerDisplayFrame question={question} rolledValues={rolledValues} wide={columns}>
      {scenario}
      <DndContext
        sensors={sensors}
        collisionDetection={sortCollisionDetection}
        accessibility={accessibility}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <div aria-live='polite' aria-atomic='true' className='sr-only'>{announcement}</div>

        {showCarryUi && (
          /* WHAT YOU ARE HOLDING, said out loud and shown. On a phone the pick-up and the drop
             happen in different tabs, so without this you arrive at the bins having forgotten what
             you picked up — or whether you picked anything up at all. Doubles as cancel. */
          <button
            type='button'
            onClick={() => { const held = carry.carrying!; carry.cancel(); setAnnouncement(t('blocks.sort.a11y.cancelled', { item: nameOf(held) })); }}
            className='flex items-center justify-between gap-2 rounded-md border border-ws-primary bg-ws-primary/10 px-3 py-1.5 text-sm text-ws-text'
          >
            <span className='min-w-0 truncate'>{t('blocks.sort.carrying', { item: nameOf(carry.carrying!) })}</span>
            <span className='shrink-0 text-xs text-ws-muted'>{t('blocks.sort.cancelCarry')}</span>
          </button>
        )}

        {isPhone ? (
          <>
            {/* The app's own segmented control, not a hand-rolled tablist: it is the same
                track-plus-raised-pill the editor uses everywhere, and it already carries the
                roving-focus keyboard behaviour a bare row of buttons does not.
                The bins stay TOGETHER behind one segment — comparing them is the task — and it is
                the source that steps aside once it has done its job. */}
            <SegmentedControl
              aria-label={t('blocks.sort.tabsLabel')}
              value={tab}
              onChange={(next) => setTab(next as 'source' | 'bins')}
              stretch
              options={[
                { id: 'source', label: t('blocks.sort.sourceHeadingCount', { count: unsorted.length }) },
                { id: 'bins', label: t('blocks.sort.binsTab', { count: Object.values(livePlacements).flat().length }) },
              ]}
            />
            {tab === 'source' ? sourceSection : <div className='flex flex-col gap-2'>{binSections}</div>}
          </>
        ) : (
          <div className={columns ? 'flex flex-row items-stretch gap-2' : 'flex flex-col gap-2'}>
            {!inverted && sourceSection}
            {columns ? binSections : <div className='flex flex-col gap-2'>{binSections}</div>}
            {inverted && sourceSection}
          </div>
        )}

        {/* The dragged chip rides in an overlay rather than in place, for the reason Ordering
            documents: an in-place row reads dnd-kit's own transform and jumps as indices shift. */}
        <DragOverlay>
          {dragging !== null ? <div className={itemClass(true)}>{itemBody(dragging)}</div> : null}
        </DragOverlay>
      </DndContext>
      {/**
        * THE FEEDBACK KEEPS THE STANDARD COLUMN even when the bins do not.
        *
        * `wide` drops `AnswerDisplayFrame`'s cap so a horizontal sort can lay its bins out across
        * the full card — that is the exception this block has, and it is about the BINS. The
        * feedback underneath is the same paragraph of prose every other block renders, and
        * stretching it to the width of four columns made it the one feedback in the app that did
        * not match the others. Re-applied here rather than by narrowing the frame, because the
        * two genuinely want different widths in the same surface.
        */}
      {children && <div className={ANSWER_COLUMN_CLASS}>{children}</div>}
    </AnswerDisplayFrame>
  );
}

/** One item: a button for tap and keyboard, a draggable for the pointer. The same element, so the
 *  three inputs cannot disagree about what is being moved. */
function SortItemButton({ id, held, disabled, className, onTap, label, children }: {
  id: string; held: boolean; disabled: boolean; className: string;
  onTap: () => void;
  /** Set only when the name has to say more than the content does — see `itemButton`. */
  label?: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <button
      ref={setNodeRef}
      type='button'
      disabled={disabled}
      onClick={onTap}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : undefined }}
      className={className}
      {...attributes}
      {...listeners}
      /* AFTER the spread, deliberately: dnd-kit sets its own `aria-pressed` for the duration of a
         drag, and what this control reports is whether the item is IN HAND — which outlasts the
         drag, and is the thing a listener needs to know. The name follows for the same reason. */
      aria-pressed={held}
      aria-label={label}
    >
      {/*
       * THE POINTER AFFORDANCE, and nothing else — `aria-hidden`, exactly as Ordering's row grip is.
       *
       * The WHOLE CARD is the drag source and the tap target both, so this is not a handle: it is
       * the only thing on the card that says it can be dragged at all. Without it a sort item read
       * as a plain button while its own AUTHORING rows, and every Ordering row a student has
       * already met, carry one. A second focusable control here would be the real mistake — it
       * would announce the item's text twice and put a button inside a button.
       *
       * No `touch-none` goes with it, deliberately: the touch sensor activates on a 220ms press,
       * which is what leaves a quick swipe free to scroll a long sort.
       */}
      <span aria-hidden='true' className='flex h-5 w-3.5 shrink-0 items-center justify-center text-ws-muted'>
        <GripVertical className='h-3.5 w-3.5' />
      </span>
      {children}
    </button>
  );
}

/** A drop target that is also just a box. `armed` is what makes it legible as a target — without
 *  it, an idle sort shows six dashed outlines and no indication of what to do with them. */
function SortZone({ id, armed, className, heading, placeHere, children }: {
  id: string; armed: boolean; className?: string;
  heading: React.ReactNode; placeHere: React.ReactNode; children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: zoneId(id) });
  const headingId = useId();
  return (
    <div
      ref={setNodeRef}
      /* A LABELLED GROUP, which is how a listener learns where an item currently sits.
         Within-bin position is deliberately never announced — it carries no meaning unless the
         teacher turned it on — so the bin is the only location there is to report, and the group
         reports it once on entry rather than every item repeating it.
         Labelled BY the heading rather than with an `aria-label` string: the heading is the bin's
         visible name, counts and variable chips included, and a name that differs from the text on
         screen is a name the person reading the screen cannot refer to. */
      role='group'
      aria-labelledby={headingId}
      className={`flex min-w-0 flex-col rounded-lg border p-2 transition-colors ${
        isOver ? 'border-ws-primary bg-ws-primary/10'
          : armed ? 'border-dashed border-ws-primary bg-ws-primary/5'
            : 'border-ws-border bg-ws-btn/30'
      } ${className ?? ''}`}
    >
      <div id={headingId}>{heading}</div>
      {children}
      {placeHere}
    </div>
  );
}
